import { Inject, Injectable, makeStateKey, PLATFORM_ID, StateKey, TransferState } from '@angular/core';
import { GeneratedConfigService } from '@shared/generated/services';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import { map, tap } from 'rxjs/operators';
import { ConfigDto } from '@shared/generated/models/config-dto';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environment';


@Injectable({ providedIn: 'root' })
export class ConfigurationService {

  public configuration?: ConfigDto; // TODO replace with get method - throw error when null
  public readonly $configuration: BehaviorSubject<ConfigDto | null> = new BehaviorSubject<ConfigDto | null>(null);  // TODO remove - shouldnt be necessary when implemented get method for config
  private readonly stateKeyConfigValue: StateKey<ConfigDto> = makeStateKey('configValue');
  private readonly stateKeyCustomCssValue: StateKey<string> = makeStateKey('customCssValue');

  constructor(@Inject(DOCUMENT) private document: Document,
              @Inject(PLATFORM_ID) private platformId: string,
              private http: HttpClient,
              private generatedService: GeneratedConfigService,
              private transferState: TransferState) {
  }

  public getConfig(): Observable<ConfigDto> {
    let request = this.generatedService.getConfig().pipe(tap((config) => isPlatformServer(this.platformId) && this.transferState.set(this.stateKeyConfigValue, config)));
    if (this.transferState.hasKey(this.stateKeyConfigValue)) {
      request = of(this.transferState.get(this.stateKeyConfigValue, null)!).pipe(tap(() => this.transferState.remove(this.stateKeyConfigValue)));
    }
    return request
      .pipe(map((config) => ({
        ...config,
        logo: config.logo.startsWith('http') ? config.logo : environment.baseUrl + config.logo
      })))
      .pipe(tap((config) => {
        this.configuration = config;
        this.$configuration.next(config);
        this.setFavIcons();
        this.setThemeColor();
      }));
  }

  public loadCustomCss(): Observable<string> {
    let request = this.http.get('/custom.css', { responseType: 'text' }).pipe(tap((result) => isPlatformServer(this.platformId) && this.transferState.set(this.stateKeyCustomCssValue, result)));
    if (this.transferState.hasKey(this.stateKeyCustomCssValue)) {
      request = of(this.transferState.get(this.stateKeyCustomCssValue, '')!).pipe(tap(() => this.transferState.remove(this.stateKeyCustomCssValue)));
    }
    return request
      .pipe(tap((css) => {
        if (css.trim().length) {
          const style = this.document.createElement('style');
          style.innerHTML = css;
          this.document.head.appendChild(style);
        }
      }));
  }

  private setThemeColor(): void {
    const metaTag = this.document.createElement('meta');
    metaTag.name = 'theme-color';
    metaTag.content = this.configuration!.themeColor;
    this.document.head.appendChild(metaTag);
  }

  // configure fav-icons in html-head
  private setFavIcons(): void {
    ['favIcon', 'favIconShortcut', 'favIconApple'].forEach((id) => {
      const link = this.document.getElementById(id);
      if (link) {
        (link as HTMLLinkElement).href = this.configuration!.favIcon;
      }
    });
  }

  /* eslint-disable camelcase */
  public buildWebManifest(title: string, shortTitle: string): void {
    const manifest = this.document.createElement('link');
    manifest.rel = 'manifest';
    manifest.crossOrigin = 'use-credentials';
    const data = {
      name: title,
      short_name: shortTitle,
      theme_color: this.configuration!.themeColor,
      // background_color: '#fafafa',
      display: 'standalone',
      scope: './',
      start_url: '/',
      icons: [] as { src: string, sizes: string, type: string, purpose: string }[]
    };

    [72, 96, 128, 152, 192, 384, 512].forEach((size) => {
      data.icons.push({
        src: this.configuration!.favIcon,
        sizes: size + 'x' + size,
        type: 'image/ico',
        purpose: 'maskable any'

      });
    });
    manifest.href = 'data:application/manifest+json,' + encodeURIComponent(JSON.stringify(data));
    this.document.head.appendChild(manifest);
  }

}
